import React, { Suspense } from 'react'
import { Routes, Route, Outlet, Navigate } from 'react-router-dom'
import FallbackSpinner from './@core/components/spinner'
import AuthGuard from 'src/@core/components/auth/AuthGuard'
import UserLayout from './layouts/UserLayout'
import BlankLayout from './@core/layouts/BlankLayout'
// import BlankLayoutWithAppBar from './@core/layouts/BlankLayoutWithAppBar'
import AclGuard from './@core/components/auth/AclGuard'
import GuestGuard from './@core/components/auth/GuestGuard'
import { buildAbilityFor, defaultACLObj } from './configs/acl'
import AuthLayout from './layouts/AuthLayout'
import ProtectedRoute from './views/common/auth/ProtectedRoute'

//website
const FaqsPage = React.lazy(() => import('./pages/website-content/faqs'))
const TermsandConditionPage = React.lazy(() => import('./pages/website-content/termsandcondition'))
const TestimonialsPage = React.lazy(() => import('./pages/website-content/testimonials'))
const ContactUsPage = React.lazy(() => import('./pages/website-content/contact-us'))
const PrivacyPolicyPage = React.lazy(() => import('./pages/website-content/privacy-policy'))
const BlogPage = React.lazy(() => import('./pages/website-content/blog'))
const OurTeamPage = React.lazy(() => import('./pages/website-content/our-team'))

//admin
const HomePage = React.lazy(() => import('./pages/home'))
const DashboardPage = React.lazy(() => import('./pages/dashboard'))
const NotificationPage = React.lazy(() => import('./pages/notification'))
const LoginPage = React.lazy(() => import('./pages/login'))
const ForgotPassword = React.lazy(() => import('./pages/login/forgotpassword'))
const UsersPage = React.lazy(() => import('./pages/users'))
const UserDetailsPage = React.lazy(() => import('./pages/users/userDetails'))
const CustomersPage = React.lazy(() => import('./pages/users/customers'))
const BusinessUserPage = React.lazy(() => import('./pages/users/businessUsers'))
const ProductCategoryPage = React.lazy(() => import('./pages/product-category'))
const ProductPackagingPage = React.lazy(() => import('./pages/product-packaging'))
const CouponsPage = React.lazy(() => import('./pages/coupons'))
const ReferralCodePage = React.lazy(() => import('./pages/referral-code'))
const CityManagementPage = React.lazy(() => import('./pages/city-management'))
const OrderServicePage = React.lazy(() => import('./pages/order-service'))
const OrderStatusPage = React.lazy(() => import('./pages/order/order-status'))
const OrderDetailsPage = React.lazy(() => import('./pages/order/order-details'))
const CashoutRequestPage = React.lazy(() => import('./pages/cashout-request'))
const InventoryPackagePage = React.lazy(() => import('./pages/inventory-package'))
const InventoryPackageRequestPage = React.lazy(() => import('./pages/inventory-package-request'))
const ProductInventoryRequestPage = React.lazy(() => import('./pages/product-inventory-request/productInventoryReq'))
const ProductInventoryReqDetailsPage = React.lazy(
  () => import('./pages/product-inventory-request/productInventoryReqDetails')
)
const ZonePage = React.lazy(() => import('./pages/zone'))
const HQAddressPage = React.lazy(() => import('./pages/hq-address'))
const PackageDimensionPage = React.lazy(() => import('./pages/package-dimension'))
const PackageWeightPage = React.lazy(() => import('./pages/package-weight'))
const LogIssuesPage = React.lazy(() => import('./pages/log-issue/logIssues'))
const MainReasonsPage = React.lazy(() => import('./pages/log-issue/mainReason'))
const SubReasonsPage = React.lazy(() => import('./pages/log-issue/subReason'))
const CommisionAmountPage = React.lazy(() => import('./pages/commission-amount'))
const BillingAndPaymentPage = React.lazy(() => import('./pages/biiling-and-payment'))
const BillingPaymentManagementPage = React.lazy(() => import('./pages/billing-payment-management'))
const SubAdminPage = React.lazy(() => import('./pages/RoleManagement/SubAdmin'))
const RolesPage = React.lazy(() => import('./pages/RoleManagement/Roles'))
const PermissionsPage = React.lazy(() => import('./pages/RoleManagement/Permissions'))
const AddEditRolePage = React.lazy(() => import('./pages/RoleManagement/Roles/AddEditRole'))
const RatingsPage = React.lazy(() => import('./pages/ratings'))
const CampaignNotificationsPage = React.lazy(() => import('./pages/campaign-notifications'))
const SMSAlertPage = React.lazy(() => import('./pages/sms-alert'))
const EmailNotificationPage = React.lazy(() => import('./pages/EmailNotification/'))
const StorageUnitPage = React.lazy(() => import('./pages/storage-unit/'))

const Page401 = React.lazy(() => import('./pages/401'))
const Page404 = React.lazy(() => import('./pages/404'))

const Guard = ({ children, authGuard, guestGuard }) => {
  if (guestGuard) {
    return <GuestGuard fallback={<FallbackSpinner />}>{children}</GuestGuard>
  } else if (!guestGuard && !authGuard) {
    return <>{children}</>
  } else {
    return <AuthGuard fallback={<FallbackSpinner />}>{children}</AuthGuard>
  }
}

function App() {
  const aclAbilities = defaultACLObj
  return (
    <Suspense fallback={<FallbackSpinner />}>
      <AclGuard aclAbilities={aclAbilities}>
        <Routes>
          <Route
            element={
              <BlankLayout>
                <Outlet />
              </BlankLayout>
            }
          >
            <Route path='/401' element={<Page401 />} />
            <Route path='/404' element={<Page404 />} />

            <Route
              element={
                <AuthLayout>
                  <Outlet />
                </AuthLayout>
              }
            >
              <Route
                element={
                  <Guard guestGuard>
                    <Outlet />
                  </Guard>
                }
              >
                <Route path='/login' element={<LoginPage />}></Route>
                <Route path='/forgot-password' element={<ForgotPassword />} />
              </Route>
            </Route>
          </Route>

          <Route
            element={
              <UserLayout>
                <Outlet />
              </UserLayout>
            }
          >
            <Route
              element={
                <Guard authGuard>
                  <Outlet />
                </Guard>
              }
            >
              <Route path='' element={<HomePage />} />
              <Route path='/dashboard' element={<DashboardPage />} />
              <Route
                path='/notifications'
                element={
                  <ProtectedRoute>
                    <NotificationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/users'
                element={
                  <ProtectedRoute>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/:users/user-details/:id'
                element={
                  <ProtectedRoute>
                    <UserDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/customers'
                element={
                  <ProtectedRoute>
                    <CustomersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/business-users'
                element={
                  <ProtectedRoute>
                    <BusinessUserPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/product-category'
                element={
                  <ProtectedRoute>
                    <ProductCategoryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/product-packaging'
                element={
                  <ProtectedRoute>
                    <ProductPackagingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/coupons'
                element={
                  <ProtectedRoute>
                    <CouponsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/referral-code'
                element={
                  <ProtectedRoute>
                    <ReferralCodePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/city-management'
                element={
                  <ProtectedRoute>
                    <CityManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/order-service'
                element={
                  <ProtectedRoute>
                    <OrderServicePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/order-status'
                element={
                  <ProtectedRoute>
                    <OrderStatusPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/order-status/details/:id'
                element={
                  <ProtectedRoute>
                    <OrderDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/cashout-request'
                element={
                  <ProtectedRoute>
                    <CashoutRequestPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/inventory-package'
                element={
                  <ProtectedRoute>
                    <InventoryPackagePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/inventory-package-request'
                element={
                  <ProtectedRoute>
                    <InventoryPackageRequestPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/product-inventory-request'
                element={
                  <ProtectedRoute>
                    <ProductInventoryRequestPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/product-inventory-request/details/:id'
                element={
                  <ProtectedRoute>
                    <ProductInventoryReqDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/zone'
                element={
                  <ProtectedRoute>
                    <ZonePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/hq-address'
                element={
                  <ProtectedRoute>
                    <HQAddressPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/package-dimension'
                element={
                  <ProtectedRoute>
                    <PackageDimensionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/package-weight'
                element={
                  <ProtectedRoute>
                    <PackageWeightPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path='/log-issues'
                element={
                  <ProtectedRoute>
                    <LogIssuesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/main-reasons'
                element={
                  <ProtectedRoute>
                    <MainReasonsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/main-reasons/sub-reasons/:id'
                element={
                  <ProtectedRoute>
                    <SubReasonsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/commission-amount'
                element={
                  <ProtectedRoute>
                    <CommisionAmountPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/billing-invoice-payment'
                element={
                  <ProtectedRoute>
                    <BillingAndPaymentPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/billing-payment-management'
                element={
                  <ProtectedRoute>
                    <BillingPaymentManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/sub-admin'
                element={
                  <ProtectedRoute>
                    <SubAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/roles'
                element={
                  <ProtectedRoute>
                    <RolesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/roles/add'
                element={
                  <ProtectedRoute>
                    <AddEditRolePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/roles/edit/:id'
                element={
                  <ProtectedRoute>
                    <AddEditRolePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/permissions'
                element={
                  <ProtectedRoute>
                    <PermissionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/ratings'
                element={
                  <ProtectedRoute>
                    <RatingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/campaign-notifications'
                element={
                  <ProtectedRoute>
                    <CampaignNotificationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/sms-alert'
                element={
                  <ProtectedRoute>
                    <SMSAlertPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/email-notification'
                element={
                  <ProtectedRoute>
                    <EmailNotificationPage />
                  </ProtectedRoute>
                }
              />

              {/* website */}
              <Route
                path='/privacy-policy'
                element={
                  <ProtectedRoute>
                    <PrivacyPolicyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/terms-conditions'
                element={
                  <ProtectedRoute>
                    <TermsandConditionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/testimonials'
                element={
                  <ProtectedRoute>
                    <TestimonialsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/contact-us'
                element={
                  <ProtectedRoute>
                    <ContactUsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/faqs'
                element={
                  <ProtectedRoute>
                    <FaqsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/blog'
                element={
                  <ProtectedRoute>
                    <BlogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/our-team'
                element={
                  <ProtectedRoute>
                    <OurTeamPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/storage-units'
                element={
                  <ProtectedRoute>
                    <StorageUnitPage />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Route>

          {/* If no route found redirect it to --> /404 */}
          <Route path='*' element={<Navigate to='/404' replace />} />
        </Routes>
      </AclGuard>
    </Suspense>
  )
}

export default App
